<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import type { Competency, CompetencyLevel, MentorshipCapabilities, ProgramActivityTemplate, ProgramStageTemplate, ProgramTemplate, ProgramTemplateVersion, QualityControlTreeItem } from '../types'
import { mentorshipApi } from '../services/mentorshipApi'
import { useMentorshipSettingsStore } from '../stores/mentorshipSettingsStore'
import MentorshipDialog from './MentorshipDialog.vue'
import { getApiError } from '@/composables/getApiError'

const templateDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const activityDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const competencyDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const versionDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const stageDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const levelsDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const router = useRouter()
const route = useRoute()
const close = () => router.push({ name: 'mentorship-cases', query: route.query })
const store = useMentorshipSettingsStore()
const capabilities = ref<MentorshipCapabilities | null>(null)
const versionDialog = ref(false)
const durationMonths = ref(12)
const editingVersion = ref<ProgramTemplateVersion | null>(null)
const templateDurationMonths = ref(12)
const stageDialog = ref(false)
const stageForm = reactive({ id: '', name: '', description: '' })
const levelsFor = ref<Competency | null>(null)
const levelForm = reactive({ id: '', value: 0, name: '', criteria: '' })
const stageVersion = ref<ProgramTemplateVersion | null>(null)
const tab = ref('templates')
const selected = ref<ProgramTemplate | null>(null)
const templateDialog = ref(false)
const competencyDialog = ref(false)
const activityDialog = ref(false)
const editingActivityId = ref<string | null>(null)
const saving = ref(false)
const error = ref<string | null>(null)
const activityTypeLabels: Record<string, string> = {
  acknowledgement: 'Ознакомление',
  material: 'Изучение материала',
  practical_task: 'Практическое задание',
  observation: 'Работа под наблюдением',
  meeting: 'Встреча',
  test: 'Тестирование',
  competency_assessment: 'Оценка компетенции',
  checkpoint: 'Промежуточный контроль',
  final: 'Итоговое заключение',
}
function versionsLabel(count: number) {
  if (count % 10 === 1 && count % 100 !== 11) return `${count} версия`
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return `${count} версии`
  return `${count} версий`
}
const templateForm = reactive({ name: '', code: '', description: '' })
const competencyForm = reactive({ code: '', name: '', group: '', description: '', is_active: true })
const normativeTree = ref<QualityControlTreeItem[]>([])
const activityForm = reactive({
  stage: '',
  name: '',
  description: '',
  activity_type: 'material',
  order: 1,
  offset_days: 0,
  is_required: true,
  completion_criteria: '',
  normative_parts: [] as string[],
})

const normativeOptions = computed(() => {
  const result: { title: string
    value: string }[] = []
  const walk = (items: QualityControlTreeItem[], parents: string[] = []) => {
    items.forEach(item => {
      const path = [...parents, item.label]
      if (!item.children?.length && item.part_type) result.push({ title: path.join(' → '), value: item.id })
      if (item.children?.length) walk(item.children, path)
    })
  }
  walk(normativeTree.value)
  return result
})

async function refresh() {
  await store.fetchCatalogs()
  if (selected.value) selected.value = store.templates.find(item => item.id === selected.value?.id) ?? null
  if (levelsFor.value) levelsFor.value = store.competencies.find(item => item.id === levelsFor.value?.id) ?? null
}

async function execute(action: () => Promise<unknown>) {
  if (!capabilities.value?.can_manage) return
  saving.value = true
  error.value = null
  try {
    await action()
    await refresh()
  } catch (caught) {
    error.value = getApiError(caught, 'Не удалось выполнить операцию')
  } finally {
    saving.value = false
  }
}

async function createTemplate() {
  if (!templateForm.name.trim() || !templateForm.code.trim() || !Number.isInteger(templateDurationMonths.value) || templateDurationMonths.value < 1) return
  await execute(async () => {
    selected.value = await mentorshipApi.createProgramTemplate(templateForm)
    await mentorshipApi.createProgramVersion({ template: selected.value.id, version: 1, duration_months: templateDurationMonths.value })
    templateDialog.value = false
    Object.assign(templateForm, { name: '', code: '', description: '' })
    templateDurationMonths.value = 12
  })
}

function openVersion(version?: ProgramTemplateVersion) {
  if (!capabilities.value?.can_manage || (version && version.status !== 'draft')) return
  editingVersion.value = version || null
  durationMonths.value = version?.duration_months || 12
  error.value = null
  versionDialog.value = true
}
function openNewVersion() {
  openVersion()
}
async function saveVersion() {
  if (!selected.value || !Number.isInteger(durationMonths.value) || durationMonths.value < 1) return
  if (editingVersion.value && editingVersion.value.status !== 'draft') return
  await execute(async () => {
    if (editingVersion.value) {
      await mentorshipApi.updateProgramVersion(editingVersion.value.id, { duration_months: durationMonths.value })
    } else {
      const version = Math.max(0, ...selected.value!.versions.map(item => item.version)) + 1
      await mentorshipApi.createProgramVersion({ template: selected.value!.id, version, duration_months: durationMonths.value })
    }
    versionDialog.value = false
  })
}

function openStage(version: ProgramTemplateVersion, stage?: ProgramStageTemplate) {
  stageVersion.value = version
  Object.assign(stageForm, { id: stage?.id || '', name: stage?.name || '', description: stage?.description || '' })
  error.value = null
  stageDialog.value = true
}
async function saveStage() {
  if (!stageVersion.value || !stageForm.name.trim()) return
  await execute(async () => {
    if (stageForm.id) await mentorshipApi.updateProgramStage(stageForm.id, { name: stageForm.name.trim(), description: stageForm.description })
    else await mentorshipApi.createProgramStage({ version: stageVersion.value!.id, name: stageForm.name.trim(), description: stageForm.description, order: stageVersion.value!.stages.length + 1 })
    stageDialog.value = false
  })
}
function openLevels(item: Competency) {
  levelsFor.value = item
  Object.assign(levelForm, { id: '', value: 0, name: '', criteria: '' })
  error.value = null
}
function editLevel(item: CompetencyLevel) {
  Object.assign(levelForm, item)
}
async function saveLevel() {
  if (!levelsFor.value || !levelForm.name.trim() || !Number.isInteger(levelForm.value) || levelForm.value < 0) return
  await execute(async () => {
    await mentorshipApi.saveCompetencyLevel({ competency: levelsFor.value!.id, value: levelForm.value, name: levelForm.name.trim(), criteria: levelForm.criteria }, levelForm.id || undefined)
    Object.assign(levelForm, { id: '', value: 0, name: '', criteria: '' })
  })
}

async function openActivityDialog(stageId: string, order: number, activity?: ProgramActivityTemplate) {
  const version = selected.value?.versions.find(item => item.stages.some(stage => stage.id === stageId))
  if (!capabilities.value?.can_manage || version?.status !== 'draft') return
  editingActivityId.value = activity?.id || null
  error.value = null
  Object.assign(activityForm, {
    stage: stageId,
    name: activity?.name || `Мероприятие ${order}`,
    description: activity?.description || '',
    activity_type: activity?.activity_type || 'material',
    order,
    offset_days: activity?.offset_days ?? 0,
    is_required: activity?.is_required ?? true,
    completion_criteria: activity?.completion_criteria || '',
    normative_parts: [...(activity?.normative_parts || [])],
  })
  activityDialog.value = true
  if (!normativeTree.value.length) {
    try {
      normativeTree.value = await mentorshipApi.getQualityControlStandards()
    } catch (e) {
      error.value = getApiError(e, 'Не удалось загрузить нормативы')
    }
  }
}

async function saveActivity() {
  if (!activityForm.name.trim() || !Number.isInteger(activityForm.offset_days) || activityForm.offset_days < 0) return
  const version = selected.value?.versions.find(item => item.stages.some(stage => stage.id === activityForm.stage))
  if (version?.status !== 'draft') return
  await execute(async () => {
    const { stage, order, ...payload } = activityForm
    if (editingActivityId.value) await mentorshipApi.updateProgramActivity(editingActivityId.value, { ...payload, name: payload.name.trim() })
    else await mentorshipApi.createProgramActivity({ ...payload, stage, order, name: payload.name.trim() })
    activityDialog.value = false
  })
}

async function publish(version: ProgramTemplateVersion) {
  await execute(() => mentorshipApi.publishProgramVersion(version.id))
}

async function createCompetency() {
  await execute(async () => {
    levelsFor.value = await mentorshipApi.createCompetency(competencyForm)
    competencyDialog.value = false
    Object.assign(competencyForm, { code: '', name: '', group: '', description: '', is_active: true })
  })
}

onBeforeRouteLeave(async () => {
  if (saving.value) return false
  for (const [open, dialog] of [[templateDialog.value, templateDialogRef.value], [activityDialog.value, activityDialogRef.value], [competencyDialog.value, competencyDialogRef.value], [versionDialog.value, versionDialogRef.value], [stageDialog.value, stageDialogRef.value], [!!levelsFor.value, levelsDialogRef.value]] as const) {
    if (open && !await dialog?.confirmDismiss()) return false
  }
  return true
})
async function load() {
  error.value = null
  try {
    capabilities.value = await mentorshipApi.getCapabilities()
    await refresh()
  } catch (e) {
    error.value = getApiError(e, 'Не удалось загрузить программы')
  }
}
onMounted(load)
</script>

<template>
  <MentorshipDialog
    model-value
    title="Программы и компетенции"
    embedded
    :busy="saving"
    :persistent="templateDialog || activityDialog || competencyDialog || versionDialog || stageDialog || !!levelsFor"
    @update:model-value="close"
  >
    <template #header-actions>
      <VBtn
        v-if="capabilities?.can_manage"
        class="general-button"
        prepend-icon="ri-add-line"
        @click="tab === 'templates' ? templateDialog = true : competencyDialog = true"
      >
        {{ tab === 'templates' ? 'Создать шаблон' : 'Создать компетенцию' }}
      </VBtn>
    </template>
    <VTabs v-model="tab" class="px-4" show-arrows>
      <VTab value="templates">
        Шаблоны программ
      </VTab><VTab value="competencies">
        Компетенции
      </VTab>
    </VTabs>
    <VCardText class="entity-form-scroll-body">
      <VAlert
        v-if="error || store.error"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        {{ error || store.error }}
        <VBtn variant="text" :disabled="store.loading || saving" @click="load">
          Повторить загрузку
        </VBtn>
      </VAlert>
      <VWindow v-model="tab">
        <VWindowItem value="templates">
          <VRow>
            <VCol cols="12" md="4">
              <VCard class="border" elevation="0">
                <VCardTitle>Шаблоны</VCardTitle><VList>
                  <VListItem
                    v-for="item in store.templates"
                    :key="item.id"
                    :title="item.name"
                    :subtitle="versionsLabel(item.versions.length)"
                    :active="selected?.id === item.id"
                    @click="selected = item"
                  />
                </VList><VCardText v-if="!store.templates.length" class="text-center text-italic">
                  Данные отсутствуют
                </VCardText>
              </VCard>
            </VCol>
            <VCol cols="12" md="8">
              <VAlert v-if="!selected" type="info" variant="tonal">
                Выберите шаблон программы
              </VAlert>
              <template v-else>
                <div class="d-flex flex-wrap gap-2 justify-space-between align-center mb-3">
                  <h3 class="text-h6">
                    {{ selected.name }}
                  </h3><VBtn
                    v-if="capabilities?.can_manage"
                    variant="outlined"
                    prepend-icon="ri-add-line"
                    @click="openNewVersion"
                  >
                    Новая версия
                  </VBtn>
                </div>
                <VExpansionPanels>
                  <VExpansionPanel v-for="version in selected.versions" :key="version.id">
                    <VExpansionPanelTitle>Версия {{ version.version }} · {{ version.duration_months }} мес. · {{ version.status === 'published' ? 'Опубликована' : version.status === 'archived' ? 'В архиве' : 'Черновик' }}</VExpansionPanelTitle>
                    <VExpansionPanelText>
                      <VCard
                        v-for="stage in version.stages"
                        :key="stage.id"
                        class="border mb-2"
                        elevation="0"
                      >
                        <VCardTitle class="d-flex justify-space-between text-subtitle-1">
                          <span>{{ stage.order }}. {{ stage.name }}</span><VBtn
                            v-if="version.status === 'draft' && capabilities?.can_manage"
                            variant="text"
                            size="small"
                            icon="ri-edit-line"
                            aria-label="Изменить этап"
                            @click="openStage(version, stage)"
                          /><VBtn
                            v-if="version.status === 'draft' && capabilities?.can_manage"
                            size="small"
                            variant="text"
                            @click="openActivityDialog(stage.id, stage.activities.length + 1)"
                          >
                            Добавить мероприятие
                          </VBtn>
                        </VCardTitle>
                        <VList density="compact">
                          <VListItem
                            v-for="activity in stage.activities"
                            :key="activity.id"
                            :title="activity.name"
                            :subtitle="`${activityTypeLabels[activity.activity_type] || activity.activity_type} · срок: ${activity.offset_days} дн. от начала${activity.is_required ? ' · обязательное' : ''}`"
                          >
                            <template #append>
                              <VBtn
                                v-if="version.status === 'draft' && capabilities?.can_manage"
                                variant="text"
                                size="small"
                                icon="ri-edit-line"
                                aria-label="Изменить мероприятие"
                                :disabled="saving"
                                @click="openActivityDialog(stage.id, activity.order, activity)"
                              />
                              <VChip v-if="activity.normative_references?.length" size="small" variant="tonal">
                                Нормативы: {{ activity.normative_references.length }}
                              </VChip>
                            </template>
                          </VListItem>
                        </VList>
                      </VCard>
                      <div v-if="version.status === 'draft' && capabilities?.can_manage" class="d-flex flex-wrap gap-2 mt-3">
                        <VBtn variant="outlined" :disabled="saving" @click="openVersion(version)">
                          Изменить длительность
                        </VBtn>
                        <VBtn variant="outlined" @click="openStage(version)">
                          Добавить этап
                        </VBtn><VBtn color="primary" :disabled="!version.stages.length || version.stages.some(stage => !stage.activities.length)" @click="publish(version)">
                          Опубликовать
                        </VBtn>
                      </div>
                    </VExpansionPanelText>
                  </VExpansionPanel>
                </VExpansionPanels>
              </template>
            </VCol>
          </VRow>
        </VWindowItem>
        <VWindowItem value="competencies">
          <VCard class="border" elevation="0">
            <VDataTable :items="store.competencies" :headers="[{ title: 'Код', key: 'code' }, { title: 'Название', key: 'name' }, { title: 'Группа', key: 'group' }, { title: 'Уровни оценки', key: 'levels', sortable: false }]" :items-per-page="10">
              <template #item.levels="{ item }">
                <VBtn
                  v-if="capabilities?.can_manage"
                  variant="text"
                  size="small"
                  @click="openLevels(item)"
                >
                  {{ item.levels.length ? `Уровней: ${item.levels.length}` : 'Настроить уровни' }}
                </VBtn><span v-else>{{ item.levels.length }}</span>
              </template>
              <template #no-data>
                <div class="text-center py-8">
                  <VIcon
                    icon="ri-medal-line"
                    size="40"
                    color="primary"
                    class="mb-2"
                  />
                  <div class="font-weight-medium">
                    Компетенции пока не добавлены
                  </div>
                  <div class="text-body-2 text-medium-emphasis mb-3">
                    Создайте первую компетенцию и задайте уровни оценки сотрудников.
                  </div>
                  <VBtn
                    v-if="capabilities?.can_manage"
                    variant="tonal"
                    prepend-icon="ri-add-line"
                    @click="competencyDialog = true"
                  >
                    Создать компетенцию
                  </VBtn>
                </div>
              </template>
            </VDataTable>
          </VCard>
        </VWindowItem>
      </VWindow>
    </VCardText>
  </MentorshipDialog>
  <MentorshipDialog
    ref="templateDialogRef"
    v-model="templateDialog"
    max-width="640"
    title="Новый шаблон"
    :busy="saving"
    :draft="[templateForm, templateDurationMonths]"
  >
    <VCardText>
      <VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-3"
      >
        {{ error }}
      </VAlert><VTextField v-model="templateForm.name" label="Название" /><VTextField v-model="templateForm.code" label="Код" /><VTextarea v-model="templateForm.description" label="Описание" /><VTextField
        v-model.number="templateDurationMonths"
        label="Продолжительность первой версии, месяцев"
        type="number"
        min="1"
      />
    </VCardText><VCardActions>
      <VSpacer /><VBtn variant="text" @click="templateDialogRef?.close()">
        Отмена
      </VBtn><VBtn :loading="saving" :disabled="!templateForm.name.trim() || !templateForm.code.trim() || !Number.isInteger(templateDurationMonths) || templateDurationMonths < 1" @click="createTemplate">
        Создать
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    ref="activityDialogRef"
    v-model="activityDialog"
    max-width="760"
    :title="editingActivityId ? 'Изменить мероприятие' : 'Новое мероприятие'"
    :busy="saving"
    :draft="activityForm"
  >
    <VCardText>
      <VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-3"
      >
        {{ error }}
      </VAlert>
      <VSelect v-model="activityForm.activity_type" :items="Object.entries(activityTypeLabels).map(([value, title]) => ({ value, title }))" label="Вид мероприятия" />
      <VTextField v-model="activityForm.name" label="Название" />
      <VTextarea v-model="activityForm.description" label="Описание" />
      <VTextarea v-model="activityForm.completion_criteria" label="Критерии завершения" />
      <VAutocomplete
        v-model="activityForm.normative_parts"
        :items="normativeOptions"
        label="Нормативные основания"
        placeholder="Выберите пункты нормативов"
        multiple
        chips
        closable-chips
      />
      <VTextField
        v-model.number="activityForm.offset_days"
        label="Срок от начала наставничества, дней"
        type="number"
        min="0"
      />
      <VCheckbox v-model="activityForm.is_required" label="Обязательное мероприятие" />
    </VCardText>
    <VCardActions>
      <VSpacer /><VBtn variant="text" :disabled="saving" @click="activityDialogRef?.close()">
        Отмена
      </VBtn>
      <VBtn :loading="saving" :disabled="!activityForm.name.trim() || !Number.isInteger(activityForm.offset_days) || activityForm.offset_days < 0" @click="saveActivity">
        {{ editingActivityId ? 'Сохранить' : 'Создать' }}
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    ref="competencyDialogRef"
    v-model="competencyDialog"
    max-width="640"
    title="Новая компетенция"
    :busy="saving"
    :draft="competencyForm"
  >
    <VCardText>
      <VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-3"
      >
        {{ error }}
      </VAlert><VTextField v-model="competencyForm.code" label="Код" /><VTextField v-model="competencyForm.name" label="Название" /><VTextField v-model="competencyForm.group" label="Группа" /><VTextarea v-model="competencyForm.description" label="Описание" />
    </VCardText><VCardActions>
      <VSpacer /><VBtn variant="text" @click="competencyDialogRef?.close()">
        Отмена
      </VBtn><VBtn :loading="saving" :disabled="!competencyForm.code || !competencyForm.name" @click="createCompetency">
        Создать
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    ref="versionDialogRef"
    v-model="versionDialog"
    max-width="460"
    :title="editingVersion ? `Длительность версии ${editingVersion.version}` : 'Новая версия программы'"
    :busy="saving"
    :draft="durationMonths"
  >
    <VCardText>
      <VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-3"
      >
        {{ error }}
      </VAlert><VTextField
        v-model.number="durationMonths"
        label="Продолжительность, месяцев"
        type="number"
        min="1"
        hint="Плановая длительность программы; срок конкретного назначения указывается в карточке."
        persistent-hint
      />
    </VCardText><VCardActions>
      <VSpacer /><VBtn :disabled="saving" @click="versionDialogRef?.close()">
        Отмена
      </VBtn><VBtn :loading="saving" :disabled="!Number.isInteger(durationMonths) || durationMonths < 1" @click="saveVersion">
        {{ editingVersion ? 'Сохранить' : 'Создать' }}
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    ref="stageDialogRef"
    v-model="stageDialog"
    max-width="600"
    :title="stageForm.id ? 'Изменить этап' : 'Новый этап'"
    :busy="saving"
    :draft="stageForm"
  >
    <VCardText>
      <VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-3"
      >
        {{ error }}
      </VAlert><VTextField v-model="stageForm.name" label="Название *" /><VTextarea v-model="stageForm.description" label="Описание" />
    </VCardText><VCardActions>
      <VSpacer /><VBtn :disabled="saving" @click="stageDialogRef?.close()">
        Отмена
      </VBtn><VBtn :loading="saving" :disabled="!stageForm.name.trim()" @click="saveStage">
        Сохранить
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    ref="levelsDialogRef"
    :model-value="!!levelsFor"
    max-width="640"
    title="Уровни оценки компетенции"
    :busy="saving"
    :draft="levelForm"
    @update:model-value="!$event && (levelsFor = null)"
  >
    <VCardText>
      <p class="mb-3">
        {{ levelsFor?.name }}
      </p><VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-3"
      >
        {{ error }}
      </VAlert><VList>
        <VListItem
          v-for="level in levelsFor?.levels"
          :key="level.id"
          :title="`${level.value} — ${level.name}`"
          :subtitle="level.criteria"
          @click="editLevel(level)"
        />
      </VList><VTextField
        v-model.number="levelForm.value"
        label="Значение уровня"
        type="number"
        min="0"
      /><VTextField v-model="levelForm.name" label="Название уровня *" /><VTextarea v-model="levelForm.criteria" label="Критерии оценки" />
    </VCardText><VCardActions>
      <VSpacer /><VBtn :disabled="saving" @click="levelsDialogRef?.close()">
        Закрыть
      </VBtn><VBtn :loading="saving" :disabled="!levelForm.name.trim() || !Number.isInteger(levelForm.value) || levelForm.value < 0" @click="saveLevel">
        {{ levelForm.id ? 'Сохранить уровень' : 'Добавить уровень' }}
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
</template>

<style scoped>
:deep(.v-list-item-title), :deep(.v-card-title) { white-space: normal; overflow-wrap: anywhere; }
</style>
